import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';
import { useSearchParams } from 'react-router-dom';
import {
  Package as PackageIcon, ShoppingCart, LogIn,
  LayoutGrid, List, Search, Shield, X,
  ChevronDown, ChevronUp, Star, Plus,
  Carrot, Droplets, Flame, Fish, Cake, Archive,
  Snowflake, Cookie, Coffee, HeartPulse, User,
} from 'lucide-react';
import ProductCard from './ProductCard';
import ProductDetailView from './ProductDetailView';
import DietaryFilterPanel from './DietaryFilterPanel';
import GroceryListHub from '../GroceryLists/GroceryListHub';
import GuestGroceryList from '../GroceryLists/GuestGroceryList';
import { PantryHubFull } from '../Pantry';
import ProductsSidebar from './ProductsSidebar';
import { useDietaryFilter, DietaryFilterProvider } from '../../contexts/DietaryFilterContext';
import { Card, Button } from '../UI';

// ─────────────────────────────────────────────────────────────
// Categories — matches iOS ProductCategory enum rawValues exactly
// These are the values stored in Firestore `category` field
// ─────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'all',       label: 'All',       icon: PackageIcon },
  { id: 'produce',   label: 'Produce',   icon: Carrot      },
  { id: 'dairy',     label: 'Dairy',     icon: Droplets    },
  { id: 'meat',      label: 'Meat',      icon: Flame       },
  { id: 'seafood',   label: 'Seafood',   icon: Fish        },
  { id: 'bakery',    label: 'Bakery',    icon: Cake        },
  { id: 'pantry',    label: 'Pantry',    icon: Archive     },
  { id: 'frozen',    label: 'Frozen',    icon: Snowflake   },
  { id: 'snacks',    label: 'Snacks',    icon: Cookie      },
  { id: 'beverages', label: 'Beverages', icon: Coffee      },
  { id: 'health',    label: 'Health',    icon: HeartPulse  },
  { id: 'personal',  label: 'Personal',  icon: User        },
];

// ─────────────────────────────────────────────────────────────
// Dietary tags — mirrors iOS DietaryTag enum
// ─────────────────────────────────────────────────────────────
const DIETARY_TAGS = [
  { value: 'vegan',        label: 'Vegan'        },
  { value: 'vegetarian',   label: 'Vegetarian'   },
  { value: 'glutenFree',   label: 'Gluten-Free'  },
  { value: 'dairyFree',    label: 'Dairy-Free'   },
  { value: 'nutFree',      label: 'Nut-Free'     },
  { value: 'keto',         label: 'Keto'         },
  { value: 'paleo',        label: 'Paleo'        },
  { value: 'lowCarb',      label: 'Low-Carb'     },
  { value: 'halal',        label: 'Halal'        },
  { value: 'kosher',       label: 'Kosher'       },
  { value: 'organic',      label: 'Organic'      },
  { value: 'sugarFree',    label: 'Sugar-Free'   },
  { value: 'wholeFood',    label: 'Whole Food'   },
  { value: 'soyFree',      label: 'Soy-Free'     },
  { value: 'eggFree',      label: 'Egg-Free'     },
];

const HIDDEN_STATUSES = ['draft', 'rejected', 'deleted', 'archived'];

// ─────────────────────────────────────────────────────────────
// Ingredient avoid filter — mirrors iOS avoidIngredients logic
// Checks ingredientsText and ingredients[].name
// ─────────────────────────────────────────────────────────────
const passesIngredientFilter = (product, avoidTerms) => {
  if (!avoidTerms || avoidTerms.length === 0) return true;
  const avoid = avoidTerms.map(t => t.toLowerCase());
  const ingredientNames = Array.isArray(product.ingredients)
    ? product.ingredients.map(i => (i.name || '').toLowerCase())
    : [];
  const ingredientsText = (product.ingredientsText || '').toLowerCase();
  return !avoid.some(term =>
    ingredientNames.some(n => n.includes(term)) || ingredientsText.includes(term)
  );
};

// ─────────────────────────────────────────────────────────────
// FeaturedRow — products with rating >= 4.0, horizontal scroll
// Mirrors iOS featuredSection
// ─────────────────────────────────────────────────────────────
const FeaturedRow = ({ products, onSelect }) => {
  const featured = useMemo(
    () => products.filter(p => (p.rating || 0) >= 4.0 || p.isFeatured).slice(0, 10),
    [products]
  );

  if (featured.length === 0) return null;

  return (
    <div className="mb-xl">
      <div className="flex items-center gap-sm mb-md">
        <div className="w-7 h-7 rounded-lg bg-[#F5A623]/15 flex items-center justify-center">
          <Star size={13} className="text-[#F5A623] fill-[#F5A623]" />
        </div>
        <h2 className="text-title-3 font-semibold text-text-primary">Featured</h2>
      </div>

      <div className="flex gap-md overflow-x-auto pb-sm scrollbar-hide -mx-xs px-xs">
        {featured.map(product => (
          <FeaturedCard key={product.id} product={product} onClick={() => onSelect(product)} />
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// FeaturedCard — 150px wide horizontal card
// Mirrors iOS FeaturedProductCard
// ─────────────────────────────────────────────────────────────

const CATEGORY_CONFIG = {
  produce:   { gradient: 'from-[#E8F4F1] to-[#C9E8E0]', iconColor: 'text-primary',   Icon: Carrot      },
  dairy:     { gradient: 'from-[#FEF3E2] to-[#FDEBD0]', iconColor: 'text-accent',    Icon: Droplets    },
  meat:      { gradient: 'from-[#F0F4FF] to-[#E0E8FF]', iconColor: 'text-[#5B7BCC]', Icon: Flame       },
  seafood:   { gradient: 'from-[#E8F4F1] to-[#D4EDE8]', iconColor: 'text-primary',   Icon: Fish        },
  bakery:    { gradient: 'from-[#FEF3E2] to-[#FDEBD0]', iconColor: 'text-accent',    Icon: Cake        },
  pantry:    { gradient: 'from-[#FEF3E2] to-[#FDEBD0]', iconColor: 'text-accent',    Icon: Archive     },
  frozen:    { gradient: 'from-[#F0F4FF] to-[#E0E8FF]', iconColor: 'text-[#5B7BCC]', Icon: Snowflake   },
  snacks:    { gradient: 'from-[#E8F4F1] to-[#D4EDE8]', iconColor: 'text-primary',   Icon: Cookie      },
  beverages: { gradient: 'from-[#E8F4F1] to-[#D4EDE8]', iconColor: 'text-primary',   Icon: Coffee      },
  health:    { gradient: 'from-[#E8F4F1] to-[#C9E8E0]', iconColor: 'text-primary',   Icon: HeartPulse  },
  personal:  { gradient: 'from-[#E8F4F1] to-[#D4EDE8]', iconColor: 'text-primary',   Icon: User        },
};
const DEFAULT_CAT = { gradient: 'from-[#E8F4F1] to-[#D4EDE8]', iconColor: 'text-primary', Icon: PackageIcon };

const getPrimaryImageURL = (product) => {
  const images = product.images;
  if (Array.isArray(images) && images.length > 0) {
    const primary = images.find(img => img.isPrimary) || images[0];
    return primary?.urls?.medium || primary?.urls?.full || primary?.urls?.thumb || null;
  }
  return product.imageURL || product.imageUrl || product.image || null;
};

const FeaturedCard = ({ product, onClick }) => {
  const cat = CATEGORY_CONFIG[(product.category || '').toLowerCase()] || DEFAULT_CAT;
  const { Icon } = cat;
  const imageUrl = getPrimaryImageURL(product);

  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 w-36 text-left group"
    >
      {/* Image */}
      <div className={`relative w-full h-28 bg-gradient-to-br ${cat.gradient} rounded-large overflow-hidden mb-sm`}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={product.name}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform"
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon size={28} className={`${cat.iconColor} opacity-50`} strokeWidth={1.2} />
          </div>
        )}

        {/* Dietary tag badge bottom-left */}
        {(product.dietaryTags || []).length > 0 && (
          <div className="absolute bottom-sm left-sm">
            <span className={`text-[9px] font-semibold px-sm py-xs bg-white rounded-full ${cat.iconColor}`}>
              {product.dietaryTags[0]}
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      {product.brand && (
        <p className="text-[10px] font-semibold text-primary uppercase tracking-wide truncate mb-xs">
          {product.brand}
        </p>
      )}
      <p className="text-caption font-semibold text-text-primary line-clamp-2 mb-xs">
        {product.name}
      </p>
      {product.rating > 0 && (
        <div className="flex items-center gap-xs">
          <Star size={9} className="fill-[#F5A623] text-[#F5A623]" />
          <span className="text-[10px] font-semibold text-text-secondary">
            {product.rating.toFixed(1)}
          </span>
        </div>
      )}
    </button>
  );
};

// ─────────────────────────────────────────────────────────────
// IngredientAvoidInput — inline tag input for avoid list
// ─────────────────────────────────────────────────────────────
const IngredientAvoidInput = ({ avoidIngredients, setAvoidIngredients }) => {
  const [inputValue, setInputValue] = useState('');

  const addTerm = () => {
    const term = inputValue.trim().toLowerCase();
    if (!term || avoidIngredients.includes(term)) { setInputValue(''); return; }
    setAvoidIngredients(prev => [...prev, term]);
    setInputValue('');
  };

  const removeTerm = (term) => setAvoidIngredients(prev => prev.filter(t => t !== term));

  return (
    <div>
      <div className="flex items-center gap-sm mb-sm">
        <div className="flex-1 flex items-center gap-xs px-md py-sm bg-surface border border-text-tertiary/15 rounded-large focus-within:border-primary transition-all">
          <input
            type="text"
            placeholder="e.g. peanuts, gluten, soy..."
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTerm(); } }}
            className="flex-1 bg-transparent text-caption text-text-primary placeholder:text-text-tertiary focus:outline-none"
          />
        </div>
        <button
          onClick={addTerm}
          className="flex items-center gap-xs px-md py-sm bg-primary/10 text-primary rounded-large text-caption font-semibold hover:bg-primary/20 transition-all"
        >
          <Plus size={13} />
          Add
        </button>
      </div>

      {avoidIngredients.length > 0 && (
        <div className="flex flex-wrap gap-xs">
          {avoidIngredients.map(term => (
            <span key={term} className="flex items-center gap-xs px-sm py-xs bg-orange-500/10 border border-orange-500/20 text-orange-600 rounded-full text-caption font-semibold">
              {term}
              <button onClick={() => removeTerm(term)} className="hover:text-orange-800 transition-colors">
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ProductsView
// ─────────────────────────────────────────────────────────────
const ProductsView = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab]               = useState('browse');
  const [products, setProducts]                 = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [selectedProduct, setSelectedProduct]   = useState(null);
  const [isAuthenticated, setIsAuthenticated]   = useState(false);
  const [viewMode, setViewMode]                 = useState('grid');
  const [searchQuery, setSearchQuery]           = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDiet, setSelectedDiet]         = useState('');
  const [showDietaryPanel, setShowDietaryPanel] = useState(false);
  const [showIngredientFilter, setShowIngredientFilter] = useState(false);
  const [avoidIngredients, setAvoidIngredients] = useState([]);

  const { selectedProfileIds, isFilterActive, filterProducts } = useDietaryFilter();

  // Read URL params on mount
  useEffect(() => {
    const diet     = searchParams.get('diet')     || '';
    const category = searchParams.get('category') || '';
    if (diet)     setSelectedDiet(diet);
    if (category) setSelectedCategory(category);
  }, [searchParams]);

  // Auth state
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsAuthenticated(!!user);
      if (!user && activeTab === 'pantry') setActiveTab('browse');
    });
    return () => unsubscribe();
  }, [activeTab]);

  // Real-time product listener — mirrors iOS isVisible + createdAt sort
  useEffect(() => {
    if (activeTab !== 'browse') return;
    setLoading(true);

    const q = query(
      collection(db, 'products'),
      orderBy('createdAt', 'desc'),
      limit(200)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(p => {
            const status = p.status?.toLowerCase();
            return !status || !HIDDEN_STATUSES.includes(status);
          });
        setProducts(data);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching products:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [activeTab]);

  const filteredProducts = useMemo(() => {
    let result = products;

    // Search — name, brand, description
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.brand || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q)
      );
    }

    // Category — matches Firestore field value exactly
    if (selectedCategory !== 'all') {
      result = result.filter(p =>
        (p.category || '').toLowerCase() === selectedCategory
      );
    }

    // Dietary tag — normalize comparison to handle casing/underscore variants
    if (selectedDiet) {
      const normalize = s => s.toLowerCase().replace(/[_\s-]/g, '');
      result = result.filter(p =>
        (p.dietaryTags || []).some(t => normalize(t) === normalize(selectedDiet))
      );
    }

    // Avoid ingredients — mirrors iOS avoidIngredients filter
    result = result.filter(p => passesIngredientFilter(p, avoidIngredients));

    // Care profile dietary filter
    result = filterProducts(result);

    return result;
  }, [products, searchQuery, selectedCategory, selectedDiet, avoidIngredients, filterProducts]);

  const hasActiveFilters =
    searchQuery.trim() ||
    selectedCategory !== 'all' ||
    isFilterActive ||
    selectedDiet ||
    avoidIngredients.length > 0;

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedDiet('');
    setAvoidIngredients([]);
    setSearchParams({});
  };

  const activeDietLabel = DIETARY_TAGS.find(t => t.value === selectedDiet)?.label || '';

  // Show featured row only when no filters are active
  const showFeatured = !hasActiveFilters && !loading;

  const tabs = [
    { id: 'browse', label: 'Browse',        icon: PackageIcon,  guestAccess: true  },
    { id: 'lists',  label: 'Shopping List', icon: ShoppingCart, guestAccess: true  },
    { id: 'pantry', label: 'My Pantry',     icon: PackageIcon,  guestAccess: false },
  ];

  return (
    <div className="max-w-7xl mx-auto">

      {/* Tabs */}
      <div className="flex gap-sm mb-xl border-b border-text-tertiary/20">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isLocked = !tab.guestAccess && !isAuthenticated;
          return (
            <button
              key={tab.id}
              onClick={() => {
                if (isLocked) { alert('Please sign in to access this feature'); return; }
                setActiveTab(tab.id);
              }}
              className={`flex items-center gap-sm px-lg py-md text-callout font-semibold transition-all relative ${
                activeTab === tab.id
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-text-secondary hover:text-primary'
              } ${isLocked ? 'opacity-50' : ''}`}
            >
              <Icon size={18} />
              {tab.label}
              {isLocked && <LogIn size={14} className="text-text-tertiary" />}
            </button>
          );
        })}
      </div>

      {activeTab === 'browse' && (
        <div className="flex gap-xl">
          <div className="flex-1 min-w-0">

            {/* Header */}
            <div className="flex items-end justify-between mb-lg">
              <div>
                <h1 className="text-display font-rounded text-primary mb-xs">Products</h1>
                <p className="text-body text-text-secondary">Browse healthy products</p>
              </div>
              <div className="flex items-center gap-xs p-xs bg-surface rounded-large border border-text-tertiary/10">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`w-9 h-9 flex items-center justify-center rounded-medium transition-all ${
                    viewMode === 'grid' ? 'bg-primary text-white shadow-sm' : 'text-text-secondary hover:text-primary'
                  }`}
                >
                  <LayoutGrid size={17} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`w-9 h-9 flex items-center justify-center rounded-medium transition-all ${
                    viewMode === 'list' ? 'bg-primary text-white shadow-sm' : 'text-text-secondary hover:text-primary'
                  }`}
                >
                  <List size={17} />
                </button>
              </div>
            </div>

            {/* Search + filter controls */}
            <div className="mb-lg">
              <div className="flex gap-sm mb-sm flex-wrap">

                {/* Search */}
                <div className="flex-1 min-w-48 flex items-center gap-sm px-md py-sm bg-surface border border-text-tertiary/15 rounded-large focus-within:border-primary transition-all">
                  <Search size={16} className="text-text-tertiary flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search products, brands..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="flex-1 bg-transparent text-body text-text-primary placeholder:text-text-tertiary focus:outline-none"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="text-text-tertiary hover:text-text-primary transition-all">
                      <X size={14} />
                    </button>
                  )}
                </div>

                {/* Dietary filter button — auth only */}
                {isAuthenticated && (
                  <button
                    onClick={() => { setShowDietaryPanel(v => !v); setShowIngredientFilter(false); }}
                    className={`flex items-center gap-sm px-md py-sm rounded-large border text-callout font-semibold transition-all ${
                      isFilterActive && selectedProfileIds.length > 0
                        ? 'bg-primary text-white border-primary'
                        : 'bg-surface border-text-tertiary/15 text-text-secondary hover:border-primary hover:text-primary'
                    }`}
                  >
                    <Shield size={16} />
                    Dietary
                    {selectedProfileIds.length > 0 && (
                      <span className={`w-5 h-5 rounded-full text-footnote flex items-center justify-center font-bold ${
                        isFilterActive ? 'bg-white/25 text-white' : 'bg-primary/10 text-primary'
                      }`}>
                        {selectedProfileIds.length}
                      </span>
                    )}
                    {showDietaryPanel ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                )}

                {/* Avoid ingredients button */}
                <button
                  onClick={() => { setShowIngredientFilter(v => !v); setShowDietaryPanel(false); }}
                  className={`flex items-center gap-sm px-md py-sm rounded-large border text-callout font-semibold transition-all ${
                    avoidIngredients.length > 0
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-surface border-text-tertiary/15 text-text-secondary hover:border-orange-400 hover:text-orange-500'
                  }`}
                >
                  <X size={16} />
                  Avoid
                  {avoidIngredients.length > 0 && (
                    <span className="w-5 h-5 rounded-full bg-white/25 text-white text-footnote flex items-center justify-center font-bold">
                      {avoidIngredients.length}
                    </span>
                  )}
                </button>

                {/* Clear all */}
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="flex items-center gap-xs px-md py-sm rounded-large border border-text-tertiary/15 text-callout text-text-secondary hover:text-error hover:border-error transition-all"
                  >
                    <X size={14} />
                    Clear
                  </button>
                )}
              </div>

              {/* Active diet tag pill */}
              {selectedDiet && (
                <div className="flex items-center gap-sm mb-sm">
                  <span className="flex items-center gap-xs px-md py-xs bg-primary/10 text-primary rounded-full text-caption font-semibold">
                    {activeDietLabel}
                    <button onClick={() => { setSelectedDiet(''); setSearchParams({}); }} className="hover:text-error transition-all">
                      <X size={12} />
                    </button>
                  </span>
                </div>
              )}

              {/* Category pills — matches iOS taxonomy */}
              <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide mb-sm">
                {CATEGORIES.map(cat => {
                  const Icon = cat.icon;
                  const isActive = selectedCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all flex-shrink-0 text-sm font-semibold border ${
                        isActive
                          ? 'bg-primary text-white border-primary shadow-md'
                          : 'bg-white border-text-tertiary/20 text-text-secondary hover:border-primary hover:text-primary hover:bg-primary/5'
                      }`}
                    >
                      <Icon size={15} />
                      {cat.label}
                    </button>
                  );
                })}
              </div>

              {/* Dietary filter panel */}
              {showDietaryPanel && isAuthenticated && (
                <DietaryFilterPanel onClose={() => setShowDietaryPanel(false)} />
              )}

              {/* Ingredient avoid panel */}
              {showIngredientFilter && (
                <div className="mb-lg p-lg rounded-large bg-white/80 backdrop-blur-sm border border-orange-200 shadow-sm">
                  <div className="flex items-center justify-between mb-md">
                    <div className="flex items-center gap-sm">
                      <div className="w-8 h-8 rounded-lg bg-orange-500/15 flex items-center justify-center">
                        <X size={15} className="text-orange-500" strokeWidth={2.5} />
                      </div>
                      <div>
                        <h3 className="text-callout font-semibold text-text-primary">Avoid Ingredients</h3>
                        <p className="text-caption text-text-tertiary">Hide products containing these</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowIngredientFilter(false)}
                      className="w-8 h-8 rounded-full hover:bg-text-tertiary/10 flex items-center justify-center"
                    >
                      <X size={16} className="text-text-secondary" />
                    </button>
                  </div>
                  <IngredientAvoidInput
                    avoidIngredients={avoidIngredients}
                    setAvoidIngredients={setAvoidIngredients}
                  />
                </div>
              )}
            </div>

            {/* Guest notice */}
            {!isAuthenticated && (
              <Card className="mb-lg bg-accent/5 border border-accent/20">
                <div className="flex items-center gap-md">
                  <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <PackageIcon size={20} className="text-accent" />
                  </div>
                  <div className="flex-1">
                    <p className="text-callout font-semibold text-text-primary mb-xs">Browsing as Guest</p>
                    <p className="text-caption text-text-secondary">
                      Sign in to save favorites, create pantries, and get personalized recommendations
                    </p>
                  </div>
                  <Button variant="outline" size="small" onClick={() => window.location.href = '/login'}>
                    Sign In
                  </Button>
                </div>
              </Card>
            )}

            {/* Featured row — only when no filters active */}
            {showFeatured && (
              <FeaturedRow products={products} onSelect={setSelectedProduct} />
            )}

            {/* All products header + count */}
            <div className="flex items-center gap-sm mb-md">
              <div className="w-7 h-7 rounded-lg bg-[#5B7BCC]/12 flex items-center justify-center">
                <LayoutGrid size={12} className="text-[#5B7BCC]" />
              </div>
              <h2 className="text-title-3 font-semibold text-text-primary">
                {searchQuery.trim() ? 'Results' : 'All Products'}
              </h2>
              {!loading && (
                <span className="ml-auto text-caption text-text-tertiary">
                  {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
                  {hasActiveFilters ? ' matching filters' : ''}
                </span>
              )}
            </div>

            {/* Product grid / list */}
            {loading ? (
              <div className="text-center py-xxxl">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-md" />
                <p className="text-callout text-text-secondary">Loading products...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-xxxl">
                <PackageIcon size={48} className="text-text-tertiary mx-auto mb-md" />
                <p className="text-callout text-text-secondary mb-md">No products found</p>
                {hasActiveFilters && (
                  <button onClick={clearAllFilters} className="text-primary text-callout font-semibold hover:underline">
                    Clear filters
                  </button>
                )}
              </div>
            ) : viewMode === 'grid' ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '24px' }}>
                {filteredProducts.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onClick={() => setSelectedProduct(product)}
                    viewMode="grid"
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {filteredProducts.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onClick={() => setSelectedProduct(product)}
                    viewMode="list"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <ProductsSidebar />
          </div>
        </div>
      )}

      {activeTab === 'lists' && (
        isAuthenticated ? <GroceryListHub /> : <GuestGroceryList />
      )}

      {activeTab === 'pantry' && isAuthenticated && (
        <PantryHubFull
          onNavigateToProducts={() => setActiveTab('browse')}
          onProductClick={product => setSelectedProduct(product)}
        />
      )}

      {selectedProduct && (
        <ProductDetailView
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
};

// Wrap with provider so ProductsView and DietaryFilterPanel share the same filter state
const ProductsViewWithProvider = () => (
  <DietaryFilterProvider>
    <ProductsView />
  </DietaryFilterProvider>
);

export default ProductsViewWithProvider;